// Pronunciation Service - Handles text-to-speech for different languages
// Supports English, Mandarin Chinese, and Cantonese

class PronunciationService {
  constructor() {
    this.speechSynthesis = window.speechSynthesis;
    this.voices = [];
    this.isInitialized = false;
    this.init();
  }

  async init() {
    if (this.speechSynthesis) {
      // Wait for voices to load
      if (this.speechSynthesis.getVoices().length > 0) {
        this.voices = this.speechSynthesis.getVoices();
        this.isInitialized = true;
      } else {
        this.speechSynthesis.addEventListener('voiceschanged', () => {
          this.voices = this.speechSynthesis.getVoices();
          this.isInitialized = true;
        });
      }
    }
  }

  // Get available voices for a specific language
  getVoicesForLanguage(language) {
    if (!this.isInitialized) return [];
    
    const languageMap = {
      'english': ['en-US', 'en-GB', 'en-AU'],
      'mandarin': ['zh-CN', 'zh-TW', 'zh-HK'],
      'cantonese': ['zh-HK', 'zh-TW']
    };

    const targetLanguages = languageMap[language] || ['en-US'];
    
    return this.voices.filter(voice => 
      targetLanguages.some(lang => voice.lang.startsWith(lang))
    );
  }

  // Speak text in the specified language
  speak(text, language = 'english', options = {}) {
    if (!this.speechSynthesis || !this.isInitialized) {
      console.warn('Speech synthesis not available');
      return;
    }

    // Stop any current speech
    this.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set language
    const languageMap = {
      'english': 'en-US',
      'mandarin': 'zh-CN',
      'cantonese': 'zh-HK'
    };
    
    utterance.lang = languageMap[language] || 'en-US';
    
    // Set voice
    const availableVoices = this.getVoicesForLanguage(language);
    if (availableVoices.length > 0) {
      utterance.voice = availableVoices[0];
    }
    
    // Set options
    utterance.rate = options.rate || 0.9;
    utterance.pitch = options.pitch || 1;
    utterance.volume = options.volume || 0.8;
    
    this.speechSynthesis.speak(utterance);
    
    return utterance;
  }

  // Stop current speech
  stop() {
    if (this.speechSynthesis) {
      this.speechSynthesis.cancel();
    }
  }

  // Check if speech synthesis is supported
  isSupported() {
    return !!window.speechSynthesis;
  }

  // Get pronunciation text based on language and word data
  getPronunciationText(word, language) {
    switch (language) {
      case 'english':
        return word.english;
      case 'mandarin':
        return word.chineseSimplified;
      case 'cantonese':
        return word.chineseTraditional;
      default:
        return word.english;
    }
  }
}

// Create singleton instance
const pronunciationService = new PronunciationService();

export default pronunciationService;
